import '@theme/product-form';
import '@theme/component-quantity-selector';
import { StandardEvents } from '@shopify/events';
import '@theme/rc-image-select';
import { materialForSelection, productMaterials } from '@theme/rc-siam-materials';

class RCPurchase extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.formComponent = this.querySelector('product-form-component');
    this.buttons = [...this.querySelectorAll('button[name="add"]')];
    this.available = this.dataset.rcAvailable !== 'false';
    this.pending = false;
    const { signal } = this.controller;

    this.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.target !== this.formComponent || event.action !== 'add' || !event.promise) return;
      this.pending = true;
      this.clearSuccess();
      this.render();
      event.promise.then((result) => {
        if (!this.isConnected) return;
        if (!result.detail?.didError) {
          this.success = true;
          this.querySelector('[data-rc-cart-link]')?.removeAttribute('hidden');
          this.successTimer = window.setTimeout(() => {
            this.success = false;
            this.render();
          }, 1800);
        }
      }).catch(() => {
        if (this.isConnected) this.showError(this.dataset.errorMessage);
      }).finally(() => {
        if (!this.isConnected) return;
        this.pending = false;
        this.render();
      });
    }, { signal });

    this.addEventListener(StandardEvents.cartError, (event) => {
      if (event.target !== this.formComponent) return;
      const message = event.code === 'SERVICE_UNAVAILABLE'
        ? this.dataset.errorMessage
        : event.detail?.description || event.error || this.dataset.errorMessage;
      queueMicrotask(() => {
        if (this.isConnected) this.showError(message);
      });
    }, { signal });

    this.addEventListener('submit', (event) => {
      if (!this.available || this.pending) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { signal });
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.clearSuccess();
  }

  clearSuccess() {
    window.clearTimeout(this.successTimer);
    this.success = false;
  }

  setAvailability(available) {
    this.available = available;
    this.dataset.rcAvailable = String(available);
    this.clearSuccess();
    this.render();
  }

  showError(message) {
    if (!message) return;
    const error = this.querySelector('[ref="addToCartTextError"]');
    if (error) {
      error.textContent = message;
      error.classList.remove('hidden');
    }
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
    this.querySelector('[data-rc-cart-link]')?.removeAttribute('hidden');
  }

  render() {
    this.setAttribute('aria-busy', String(this.pending));
    for (const button of this.buttons || []) {
      button.disabled = this.pending || !this.available;
      const copy = button.querySelector('[data-rc-submit-copy]');
      if (!copy) continue;
      const label = this.pending ? button.dataset.labelBusy
        : this.closest('rc-product')?.dataset.rcSelection === 'incomplete' ? button.dataset.labelChoose
          : !this.available ? button.dataset.labelSoldOut
          : this.success ? button.dataset.labelAdded : button.dataset.labelReady;
      if (label) copy.textContent = label;
    }
  }
}

class RCProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.variantSelect = this.querySelector('[data-rc-variant]');
    this.configurator = this.querySelector('rc-product-configurator');
    this.quantitySelector = this.querySelector('quantity-selector-component');
    this.quantityInput = this.quantitySelector?.querySelector('input[name="quantity"]');
    this.purchase = this.querySelector('rc-purchase');
    this.zoom = this.querySelector('[data-rc-zoom]');
    const { signal } = this.controller;

    this.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-rc-question], [data-rc-leasing-enquiry]')) this.updateEnquiry();
    }, { signal });

    this.variantSelect?.addEventListener('change', () => this.selectVariant(), { signal });
    this.quantityInput?.addEventListener('input', () => this.updateEnquiry(), { signal });
    this.quantityInput?.addEventListener('change', () => this.updateEnquiry(), { signal });
    this.addEventListener('quantity-selector:update', () => this.updateEnquiry(), { signal });
    this.addEventListener('submit', (event) => {
      if (this.configurator && (!this.configurationReady || !this.purchase?.available)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then((result) => {
        if (!this.isConnected || !Array.isArray(result.detail?.items)) return;
        const items = result.detail.items;
        for (const option of this.variantSelect?.options || []) {
          option.dataset.cartQuantity = String(items.reduce((count, item) =>
            count + (String(item.variant_id) === option.value ? Number(item.quantity) : 0), 0));
        }
        const id = this.querySelector('[data-rc-variant-id]')?.value;
        const count = items.reduce((total, item) =>
          total + (String(item.variant_id) === id ? Number(item.quantity) : 0), 0);
        this.quantitySelector?.setCartQuantity(count);
        if (this.configuredVariant) this.configuredVariant.dataset.cartQuantity = String(count);
      }).catch(() => {});
    }, { signal });

    if (this.configurator) this.holdConfiguration();
    else if (this.variantSelect) this.selectVariant(false, false);
    else {
      this.configureQuantity(this.purchase?.dataset.rcAvailable !== 'false', this.dataset.singleMax);
      this.updateEnquiry();
    }
    window.RancoldVAT?.apply(this);
    if (productMaterials(this).length) customElements.whenDefined('rc-product-gallery').then(() => {
      if (!this.isConnected) return;
      const option = this.configuredVariant || this.variantSelect?.selectedOptions[0];
      if (option?.dataset.featuredMediaId) this.selectMedia(option.dataset.featuredMediaId);
      else this.previewMaterial(option?.dataset.variantTitle || this.configurator?.selections || '');
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.zoom?.open) this.zoom.close();
  }

  selectMedia(id) {
    const gallery = this.querySelector('rc-product-gallery');
    if (typeof gallery?.selectMedia === 'function') {
      gallery.selectMedia(id);
      return;
    }
    const selected = [...this.querySelectorAll('[data-rc-media]')].find((media) => media.dataset.rcMedia === id);
    if (!selected) return;
    this.querySelectorAll('[data-rc-media]').forEach((media) => {
      const active = media === selected;
      media.hidden = !active;
      media.classList.toggle('is-active', active);
      if (!active) media.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.querySelectorAll('[data-rc-thumbnail]').forEach((thumbnail) => {
      const active = thumbnail.dataset.rcThumbnail === id;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', String(active));
    });
  }

  previewMaterial(selection) {
    const materials = productMaterials(this);
    if (!materials.length) return;
    const gallery = this.querySelector('rc-product-gallery');
    const material = materialForSelection(materials, selection);
    if (material) gallery?.selectImage?.(material);
    else gallery?.restoreInitialMedia?.();
  }

  updatePrice(element, excludingVat, includingVat) {
    if (!element) return;
    element.dataset.rcPriceExcl = excludingVat || '';
    element.dataset.rcPriceIncl = includingVat || '';
    element.textContent = window.RancoldVAT?.getMode() === 'incl' ? includingVat : excludingVat;
    window.RancoldVAT?.apply(element);
  }

  configureQuantity(available, maximum, minimum, increment, cartQuantity) {
    const input = this.quantityInput;
    const selector = this.quantitySelector;
    if (!input || !selector) return;
    selector.serverDisabledMinus = !available;
    selector.serverDisabledPlus = !available;
    input.disabled = !available;
    input.dataset.min = minimum || input.min || '1';
    if (cartQuantity !== undefined) selector.setCartQuantity(Number(cartQuantity) || 0);
    selector.updateConstraints(minimum || input.min || '1', maximum || null, increment || input.step || '1');
    if (!available) {
      selector.refs.minusButton.disabled = true;
      selector.refs.plusButton.disabled = true;
    }
  }

  selectVariant(updateHistory = true, resetQuantity = true) {
    const option = this.variantSelect?.selectedOptions[0];
    if (!option) return;
    this.applyVariantOption(option, updateHistory, resetQuantity);
  }

  holdConfiguration() {
    this.configurationReady = false;
    this.configuredVariant = null;
    this.dataset.rcSelection = 'incomplete';
    const hiddenId = this.querySelector('[data-rc-variant-id]');
    if (hiddenId) {
      hiddenId.value = '';
      hiddenId.disabled = true;
    }
    this.querySelector('[data-rc-price-region]')?.setAttribute('hidden', '');
    this.querySelector('[data-rc-delivery]')?.setAttribute('hidden', '');
    this.querySelectorAll('[data-rc-sku-wrapper]').forEach((wrapper) => { wrapper.hidden = true; });
    const pickup = this.querySelector('[data-rc-pickup]');
    if (pickup) pickup.hidden = true;
    this.purchase?.setAvailability(false);
    this.configureQuantity(false);
    this.updateEnquiry();
  }

  applyConfiguredVariant(state, freshProduct, updateHistory = true) {
    if (!state?.id) return;
    const dataset = Object.fromEntries(Object.entries(state).map(([key, value]) => [key, value == null ? '' : String(value)]));
    this.configuredVariant = { value: String(state.id), dataset };
    this.configurationReady = true;
    this.dataset.rcSelection = 'ready';
    const hiddenId = this.querySelector('[data-rc-variant-id]');
    if (hiddenId) hiddenId.disabled = false;
    this.querySelector('[data-rc-price-region]')?.removeAttribute('hidden');
    this.querySelector('[data-rc-delivery]')?.removeAttribute('hidden');
    this.applyVariantOption(this.configuredVariant, updateHistory, true);
    const pickup = this.querySelector('[data-rc-pickup]');
    const freshPickup = freshProduct?.querySelector('[data-rc-pickup]');
    if (pickup) {
      if (freshPickup) pickup.replaceChildren(...[...freshPickup.childNodes].map((node) => node.cloneNode(true)));
      pickup.hidden = !freshPickup && !pickup.childNodes.length;
    }
  }

  applyVariantOption(option, updateHistory = true, resetQuantity = true) {
    const available = option.dataset.available === 'true';
    const hiddenId = this.querySelector('[data-rc-variant-id]');
    if (hiddenId) hiddenId.value = option.value;
    this.updatePrice(this.querySelector('[data-rc-price]'), option.dataset.priceExcl, option.dataset.priceIncl);
    const comparePrice = this.querySelector('[data-rc-compare-price]');
    this.updatePrice(comparePrice, option.dataset.comparePriceExcl, option.dataset.comparePriceIncl);
    if (comparePrice) comparePrice.hidden = option.dataset.hasComparePrice !== 'true';
    this.querySelectorAll('[data-rc-sku]').forEach((sku) => { sku.textContent = option.dataset.sku || ''; });
    this.querySelectorAll('[data-rc-sku-wrapper]').forEach((wrapper) => { wrapper.hidden = !option.dataset.sku; });
    const delivery = this.querySelector('[data-rc-delivery]');
    if (delivery) delivery.dataset.stockState = option.dataset.stockState;
    const copy = this.querySelector('[data-rc-delivery-copy]');
    if (copy) copy.textContent = option.dataset.deliveryText?.trim() || '';
    const pickup = this.querySelector('[data-rc-pickup]');
    const pickupTemplate = [...this.querySelectorAll('[data-rc-pickup-template]')]
      .find((template) => template.dataset.rcPickupTemplate === option.value);
    if (pickup && pickupTemplate) pickup.replaceChildren(pickupTemplate.content.cloneNode(true));
    const form = this.querySelector('product-form-component');
    if (form) form.dataset.quantityDefault = option.dataset.quantityMin || '1';
    if (resetQuantity && this.quantityInput) this.quantityInput.value = option.dataset.quantityMin || '1';
    if (this.quantitySelector) this.quantitySelector.dataset.variantId = option.value;
    this.configureQuantity(available, option.dataset.quantityMax, option.dataset.quantityMin, option.dataset.quantityIncrement, option.dataset.cartQuantity);
    this.purchase?.setAvailability(available);
    const error = this.querySelector('[ref="addToCartTextError"]');
    error?.classList.add('hidden');
    if (option.dataset.featuredMediaId) this.selectMedia(option.dataset.featuredMediaId);
    else this.previewMaterial(option.dataset.variantTitle || option.textContent);
    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.delete('option_values');
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }
    this.updateEnquiry();
  }

  updateEnquiry() {
    const links = this.querySelectorAll('[data-rc-question], [data-rc-leasing-enquiry]');
    if (!links.length) return;
    const selectionReady = !this.configurator || this.configurationReady;
    const option = selectionReady ? this.configuredVariant || this.variantSelect?.selectedOptions[0] : null;
    const productUrl = new URL(this.dataset.productUrl, window.location.origin);
    const variantId = selectionReady ? this.querySelector('[data-rc-variant-id]')?.value : '';
    if (variantId) productUrl.searchParams.set('variant', variantId);
    const sku = selectionReady ? this.querySelector('[data-rc-sku]')?.textContent?.trim() : '';
    const lines = [`${this.dataset.enquiryProduct}: ${this.dataset.productTitle}`];
    if (option) lines.push(`${this.dataset.enquiryVariant}: ${option.dataset.variantTitle || option.textContent.trim()}`);
    if (sku) lines.push(`${this.dataset.enquirySku}: ${sku}`);
    lines.push(`${this.dataset.enquiryQuantity}: ${this.quantityInput?.value || '1'}`);
    lines.push(`${this.dataset.enquiryLink}: ${productUrl.href}`);
    for (const link of links) {
      const leasing = link.hasAttribute('data-rc-leasing-enquiry');
      const subject = `${leasing ? this.dataset.leasingLabel : this.dataset.enquiryLabel} ${this.dataset.productTitle}`;
      const destination = leasing ? link.dataset.rcLeasingUrl : this.dataset.enquiryUrl;
      if (destination) {
        const url = new URL(destination, window.location.origin);
        if (leasing) url.searchParams.set('arende', 'leasing');
        url.searchParams.set('produkt', this.dataset.productTitle);
        if (option) url.searchParams.set('variantnamn', option.dataset.variantTitle || option.textContent.trim());
        if (variantId) url.searchParams.set('variant', variantId);
        if (sku) url.searchParams.set('sku', sku);
        url.searchParams.set('antal', this.quantityInput?.value || '1');
        url.searchParams.set('produktlank', `${productUrl.pathname}${productUrl.search}`);
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);
        url.hash = leasing ? '' : 'rc-contact-form';
        link.href = url.href;
      } else {
        link.href = `mailto:${this.dataset.enquiryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\r\n'))}`;
      }
    }
  }
}

if (!customElements.get('rc-purchase')) customElements.define('rc-purchase', RCPurchase);
if (!customElements.get('rc-product')) customElements.define('rc-product', RCProduct);

